import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Check,
  Crosshair,
  HeartPulse,
  LockKeyhole,
  MoonStar,
  RotateCcw,
  Search,
  Ship,
  UserRound,
  VenetianMask,
  Vote,
} from "lucide-react";
import {
  type FormEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { PlayableRoomGameId } from "../roomGameCatalog";
import "../styles/game-tutorial.css";

const navalTutorialCells = [
  "a1",
  "a2",
  "a3",
  "a4",
  "a5",
  "b1",
  "b2",
  "b3",
  "b4",
  "b5",
  "c1",
  "c2",
  "c3",
  "c4",
  "c5",
  "d1",
  "d2",
  "d3",
  "d4",
  "d5",
  "e1",
  "e2",
  "e3",
  "e4",
  "e5",
];
const tutorialPips = ["one", "two", "three", "four", "five", "six"];
const ludoRedTutorialPath = [91, 92, 93, 94] as const;
const sunRayAngles = [
  0,
  Math.PI / 4,
  Math.PI / 2,
  (3 * Math.PI) / 4,
  Math.PI,
  (5 * Math.PI) / 4,
  (3 * Math.PI) / 2,
  (7 * Math.PI) / 4,
];

function TutorialFrame({
  title,
  prompt,
  complete,
  onReset,
  children,
}: {
  title: string;
  prompt: string;
  complete: boolean;
  onReset: () => void;
  children: ReactNode;
}) {
  return (
    <section className={`interactive-tutorial ${complete ? "complete" : ""}`}>
      <div className="tutorial-coach" aria-live="polite">
        <span className="tutorial-coach-avatar">
          <Bot />
        </span>
        <div>
          <span className="eyebrow">GUIDED PRACTICE</span>
          <h3>{title}</h3>
          <p>{prompt}</p>
        </div>
        {complete ? (
          <Check className="tutorial-complete-mark" />
        ) : (
          <span className="tutorial-step-pulse" />
        )}
      </div>
      <div className="tutorial-stage">{children}</div>
      <button className="tutorial-reset" type="button" onClick={onReset}>
        <RotateCcw /> Start this lesson again
      </button>
    </section>
  );
}

function MafiaTutorial() {
  const [step, setStep] = useState(0);
  const complete = step >= 3;
  const prompts = [
    "Roles are secret. Tap the Detective dossier to learn its night power.",
    "It is night. Investigate the suspicious player without revealing your role.",
    "At daybreak, use your private result and vote for the Mafia suspect.",
    "Case closed. You used private information without exposing it too early.",
  ];
  return (
    <TutorialFrame
      title="A night in Topzle Town"
      prompt={prompts[step] ?? prompts[3] ?? "Follow the case."}
      complete={complete}
      onReset={() => setStep(0)}
    >
      <div className={`mafia-tutorial-scene step-${step}`}>
        <div className="mafia-tutorial-skyline" aria-hidden="true">
          <MoonStar />
        </div>
        <div className="mafia-tutorial-roles">
          {[
            { id: "mafia", label: "Mafia", icon: VenetianMask },
            { id: "detective", label: "Detective", icon: Search },
            { id: "doctor", label: "Doctor", icon: HeartPulse },
            { id: "citizen", label: "Citizen", icon: UserRound },
          ].map((role) => {
            const Icon = role.icon;
            return (
              <button
                type="button"
                className={`mafia-tutorial-role ${role.id}`}
                onClick={() => step === 0 && role.id === "detective" && setStep(1)}
                disabled={step !== 0 || role.id !== "detective"}
                key={role.id}
              >
                <Icon />
                <strong>{role.label}</strong>
              </button>
            );
          })}
        </div>
        {step >= 1 && (
          <div className="mafia-tutorial-action">
            <Search />
            <span>
              {step === 1 ? "Choose a player to investigate" : "Private result: Rowan is Mafia"}
            </span>
            <button type="button" onClick={() => setStep(Math.min(3, step + 1))}>
              {step === 1 ? (
                <>
                  <Crosshair /> Investigate Rowan
                </>
              ) : (
                <>
                  <Vote /> Vote Rowan
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </TutorialFrame>
  );
}

function TicTacToeTutorial() {
  const cells = [
    "top-left",
    "top-middle",
    "top-right",
    "middle-left",
    "centre",
    "middle-right",
    "bottom-left",
    "bottom-middle",
    "bottom-right",
  ];
  const [stage, setStage] = useState<0 | 1 | 2 | 3>(0);
  useEffect(() => {
    if (stage !== 1) return;
    const timer = window.setTimeout(() => setStage(2), 650);
    return () => window.clearTimeout(timer);
  }, [stage]);
  const board = [
    "X",
    stage >= 1 ? "X" : null,
    stage >= 3 ? "X" : null,
    null,
    "O",
    null,
    null,
    null,
    stage >= 2 ? "O" : null,
  ] as Array<"X" | "O" | null>;
  const complete = stage === 3;
  return (
    <TutorialFrame
      title="Finish the line"
      prompt={
        stage === 0
          ? "You are X. Tap the highlighted top-middle square to build a two-mark threat."
          : stage === 1
            ? "The bot is responding in the bottom-right corner."
            : complete
              ? "Three X marks across the top wins immediately. You found the forced finish."
              : "The bot did not block your line. Tap the highlighted top-right square to win."
      }
      complete={complete}
      onReset={() => setStage(0)}
    >
      <fieldset className="tutorial-ttt" aria-label="Practice tic tac toe board">
        {board.map((mark, index) => (
          <button
            key={cells[index]}
            type="button"
            disabled={Boolean(mark) || complete || stage === 1}
            className={(stage === 0 && index === 1) || (stage === 2 && index === 2) ? "hint" : ""}
            onClick={() =>
              stage === 0 && index === 1
                ? setStage(1)
                : stage === 2 && index === 2
                  ? setStage(3)
                  : undefined
            }
          >
            {mark}
          </button>
        ))}
      </fieldset>
    </TutorialFrame>
  );
}

const chessFiles = ["a", "b", "c", "d", "e", "f", "g", "h"];
type TutorialChessPiece = { symbol: string; side: "white" | "black" };
const chessLessons: Array<{
  title: string;
  instruction: string;
  success: string;
  from: string;
  to?: string;
  pieces: Record<string, TutorialChessPiece>;
  castle?: boolean;
}> = [
  {
    title: "1. Read a coordinate",
    instruction: "Files a–h run along the bottom; ranks 1–8 run up the right. Tap e4.",
    success: "Exactly: e4 means file e, rank 4. Every move is recorded with these coordinates.",
    from: "e4",
    pieces: {},
  },
  {
    title: "2. Move a pawn",
    instruction: "Select the white pawn on e2, then move it two squares to e4.",
    success: "A pawn may advance two squares only from its starting rank; it captures diagonally.",
    from: "e2",
    to: "e4",
    pieces: { e2: { symbol: "♙", side: "white" }, e7: { symbol: "♟", side: "black" } },
  },
  {
    title: "3. Develop a knight",
    instruction: "Select the knight on g1, then jump in an L shape to f3.",
    success: "Knights move two squares in one direction and one sideways—and can jump over pieces.",
    from: "g1",
    to: "f3",
    pieces: { g1: { symbol: "♘", side: "white" }, g2: { symbol: "♙", side: "white" } },
  },
  {
    title: "4. Aim a bishop",
    instruction: "Select the bishop on c1, then follow its diagonal to g5.",
    success: "A bishop moves any clear distance diagonally and always remains on the same colour.",
    from: "c1",
    to: "g5",
    pieces: { c1: { symbol: "♗", side: "white" }, h6: { symbol: "♟", side: "black" } },
  },
  {
    title: "5. Use a rook",
    instruction: "Select the rook on a1, then move straight up the file to a6.",
    success: "Rooks travel along ranks and files, but cannot jump over another piece.",
    from: "a1",
    to: "a6",
    pieces: { a1: { symbol: "♖", side: "white" }, h8: { symbol: "♜", side: "black" } },
  },
  {
    title: "6. Use the queen",
    instruction: "Select the queen on d1, then move diagonally to h5.",
    success: "The queen combines rook and bishop movement, making her powerful but valuable.",
    from: "d1",
    to: "h5",
    pieces: { d1: { symbol: "♕", side: "white" }, e8: { symbol: "♚", side: "black" } },
  },
  {
    title: "7. Keep the king safe",
    instruction: "Select the king on e1, then move one safe square to e2.",
    success: "The king moves one square and may never move onto an attacked square.",
    from: "e1",
    to: "e2",
    pieces: { e1: { symbol: "♔", side: "white" }, e8: { symbol: "♚", side: "black" } },
  },
  {
    title: "8. Castle kingside",
    instruction: "The path is clear and neither piece has moved. Select e1, then tap g1.",
    success:
      "Castling moves the king to g1 and the rook to f1. It is illegal through check or out of check.",
    from: "e1",
    to: "g1",
    castle: true,
    pieces: {
      e1: { symbol: "♔", side: "white" },
      h1: { symbol: "♖", side: "white" },
      e8: { symbol: "♚", side: "black" },
    },
  },
  {
    title: "9. Deliver checkmate",
    instruction: "Select the queen on g7 and move to h7, covering the black king’s last squares.",
    success: "Checkmate: the king is attacked and has no legal escape. The game ends immediately.",
    from: "g7",
    to: "h7",
    pieces: {
      g7: { symbol: "♕", side: "white" },
      f6: { symbol: "♔", side: "white" },
      h8: { symbol: "♚", side: "black" },
    },
  },
];

function ChessTutorial() {
  const [lessonIndex, setLessonIndex] = useState(0);
  const [stage, setStage] = useState<0 | 1 | 2>(0);
  const lesson = chessLessons[lessonIndex];
  if (!lesson) return null;
  const complete = stage === 2;
  const pieces = { ...lesson.pieces };
  if (complete && lesson.to) {
    const movingPiece = pieces[lesson.from];
    delete pieces[lesson.from];
    if (movingPiece) pieces[lesson.to] = movingPiece;
    if (lesson.castle) {
      delete pieces.h1;
      pieces.f1 = { symbol: "♖", side: "white" };
    }
  }
  const reset = () => setStage(0);
  const chooseLesson = (next: number) => {
    setLessonIndex(Math.max(0, Math.min(chessLessons.length - 1, next)));
    setStage(0);
  };
  return (
    <TutorialFrame
      title={lesson.title}
      prompt={
        complete
          ? lesson.success
          : stage === 1
            ? `Selected ${lesson.from}. Now tap ${lesson.to}.`
            : lesson.instruction
      }
      complete={complete}
      onReset={reset}
    >
      <div className="tutorial-chess-lesson">
        <div className="tutorial-chess-shell">
          <fieldset className="tutorial-chess" aria-label="Practice chess board">
            {Array.from({ length: 64 }, (_, index) => {
              const rank = 8 - Math.floor(index / 8);
              const file = chessFiles[index % 8];
              const square = `${file}${rank}`;
              const target =
                !complete &&
                ((stage === 0 && square === lesson.from) || (stage === 1 && square === lesson.to));
              const piece = pieces[square];
              return (
                <button
                  key={square}
                  type="button"
                  data-side={piece?.side}
                  className={`${(index + Math.floor(index / 8)) % 2 ? "dark" : "light"} ${target ? "hint" : ""} ${stage === 1 && square === lesson.from ? "selected" : ""}`}
                  onClick={() => {
                    if (stage === 0 && square === lesson.from) setStage(lesson.to ? 1 : 2);
                    else if (stage === 1 && square === lesson.to) setStage(2);
                  }}
                  aria-label={`${square}${piece ? `, ${piece.side} piece` : ""}`}
                >
                  {piece?.symbol}
                </button>
              );
            })}
          </fieldset>
          <div className="tutorial-chess-ranks" aria-hidden="true">
            {[8, 7, 6, 5, 4, 3, 2, 1].map((rank) => (
              <span key={rank}>{rank}</span>
            ))}
          </div>
          <div className="tutorial-chess-files" aria-hidden="true">
            {chessFiles.map((file) => (
              <span key={file}>{file}</span>
            ))}
          </div>
        </div>
        <div className="tutorial-lesson-nav">
          <button
            type="button"
            disabled={lessonIndex === 0}
            onClick={() => chooseLesson(lessonIndex - 1)}
          >
            <ArrowLeft /> Previous
          </button>
          <span>
            {lessonIndex + 1} / {chessLessons.length}
          </span>
          <button
            type="button"
            disabled={!complete || lessonIndex === chessLessons.length - 1}
            onClick={() => chooseLesson(lessonIndex + 1)}
          >
            Next <ArrowRight />
          </button>
        </div>
      </div>
    </TutorialFrame>
  );
}

function LudoTutorial() {
  const [stage, setStage] = useState<0 | 1 | 2 | 3 | 4>(0);
  const [moveStep, setMoveStep] = useState(0);
  const moveComplete = stage === 4 && moveStep === ludoRedTutorialPath.length - 1;
  const tokenCell =
    stage >= 4 ? ludoRedTutorialPath[moveStep] : stage >= 2 ? ludoRedTutorialPath[0] : null;

  useEffect(() => {
    if (stage !== 4 || moveStep >= ludoRedTutorialPath.length - 1) return;
    const timer = window.setTimeout(() => setMoveStep((step) => step + 1), 360);
    return () => window.clearTimeout(timer);
  }, [moveStep, stage]);
  const cellClass = (row: number, column: number) => {
    const classes = ["tutorial-ludo-cell"];
    if (row < 6 && column < 6) classes.push("red-yard");
    else if (row < 6 && column > 8) classes.push("green-yard");
    else if (row > 8 && column < 6) classes.push("blue-yard");
    else if (row > 8 && column > 8) classes.push("yellow-yard");
    else classes.push("track");
    if (row === 7 && column > 0 && column < 6) classes.push("red-lane");
    if (column === 7 && row > 0 && row < 6) classes.push("green-lane");
    if (row === 7 && column > 8 && column < 14) classes.push("yellow-lane");
    if (column === 7 && row > 8 && row < 14) classes.push("blue-lane");
    if (row >= 6 && row <= 8 && column >= 6 && column <= 8) classes.push("home");
    if (
      (row === 6 && column === 1) ||
      (row === 2 && column === 8) ||
      (row === 8 && column === 13) ||
      (row === 12 && column === 6)
    )
      classes.push("start");
    return classes.join(" ");
  };
  const reset = () => {
    setMoveStep(0);
    setStage(0);
  };
  return (
    <TutorialFrame
      title="Release and move a goti"
      prompt={
        stage === 0
          ? "This is the full Ludo board: four yards, one shared track, safe stars, coloured home lanes, and the centre. Tap the die to roll six."
          : stage === 1
            ? "A six can release a goti. Tap the pulsing red goti in your yard to place it on the red arrow square."
            : stage === 2
              ? "Your goti entered on red’s starting square. The six grants another roll—tap the die again to roll three."
              : stage === 3
                ? "Tap the pulsing goti. It will count three track squares one at a time."
                : moveComplete
                  ? "Good: the goti moved exactly three spaces. In a match, compare every legal goti before choosing safety, capture, or progress."
                  : `Count with the goti: ${moveStep} of 3 spaces.`
      }
      complete={moveComplete}
      onReset={reset}
    >
      <div className="tutorial-ludo">
        <fieldset className="tutorial-ludo-board" aria-label="Full practice Ludo board">
          {Array.from({ length: 225 }, (_, index) => {
            const row = Math.floor(index / 15);
            const column = index % 15;
            const yardToken = index === 32 && stage < 2;
            const boardToken = tokenCell === index;
            return (
              <span className={cellClass(row, column)} key={`ludo-${row}-${column}`}>
                {yardToken ? (
                  <button
                    type="button"
                    aria-label="Release red goti"
                    className={stage === 1 ? "tutorial-goti red hint" : "tutorial-goti red"}
                    disabled={stage !== 1}
                    onClick={() => setStage(2)}
                  />
                ) : null}
                {boardToken ? (
                  <button
                    type="button"
                    aria-label="Move red goti three spaces"
                    className={stage === 3 ? "tutorial-goti red hint" : "tutorial-goti red"}
                    disabled={stage !== 3}
                    onClick={() => {
                      setMoveStep(0);
                      setStage(4);
                    }}
                  />
                ) : null}
              </span>
            );
          })}
          <span className="tutorial-ludo-home-mark">HOME</span>
        </fieldset>
        <div className="tutorial-ludo-controls">
          <button
            type="button"
            className={`tutorial-die ${stage === 0 || stage === 2 ? "hint" : ""} ${stage >= 2 ? "three" : "six"}`}
            disabled={stage !== 0 && stage !== 2}
            onClick={() => setStage(stage === 0 ? 1 : 3)}
            aria-label={stage < 2 ? "Roll a six" : "Roll a three"}
          >
            {tutorialPips.slice(0, stage >= 2 ? 3 : 6).map((pip) => (
              <i key={pip} />
            ))}
          </button>
          <span>
            <strong>Red practice turn</strong>
            <small>{stage < 2 ? "Roll: 6" : "Roll: 3"}</small>
          </span>
        </div>
      </div>
    </TutorialFrame>
  );
}

function ConnectFourTutorial() {
  const [stage, setStage] = useState<0 | 1>(0);
  return (
    <TutorialFrame
      title="Complete a real line of four"
      prompt={
        stage === 0
          ? "You are blue and already have three connected discs along the bottom. Tap column 4 to drop the winning fourth disc."
          : "Four blue discs touch horizontally, so blue wins immediately. Four can also connect vertically or diagonally."
      }
      complete={stage === 1}
      onReset={() => setStage(0)}
    >
      <fieldset className="tutorial-connect" aria-label="Practice Connect Four grid">
        {Array.from({ length: 42 }, (_, index) => {
          const row = Math.floor(index / 7);
          const column = index % 7;
          const blue = (row === 5 && column < 3) || (stage === 1 && row === 5 && column === 3);
          const yellow = (row === 5 && column === 5) || (row === 4 && column === 5);
          return (
            <button
              key={`connect-${row}-${column}`}
              type="button"
              className={`${column === 3 && stage === 0 ? "hint" : ""} ${blue ? `blue ${stage === 1 && row === 5 && column < 4 ? "winning" : ""}` : yellow ? "yellow" : ""}`}
              disabled={stage !== 0}
              onClick={() => column === 3 && setStage(1)}
              aria-label={`Column ${column + 1}`}
            />
          );
        })}
      </fieldset>
    </TutorialFrame>
  );
}

function BattleshipTutorial() {
  const [stage, setStage] = useState<0 | 1 | 2 | 3 | 4 | 5>(0);
  const shipCells = useMemo(() => new Set([11, 12]), []);
  return (
    <TutorialFrame
      title="Place, lock, fire, and sink"
      prompt={
        stage === 0
          ? "Select the two-block patrol boat below your fleet. Ships occupy several connected grid blocks."
          : stage === 1
            ? "Place it horizontally starting at C2. The highlighted C2–C3 blocks show where its hull will fit."
            : stage === 2
              ? "Your patrol boat is placed without overlap. Lock the fleet so combat can begin."
              : stage === 3
                ? "Training intel says an enemy patrol boat begins at B4. Fire at B4, then follow the hit along the row."
                : stage === 4
                  ? "B4 is a hit. Ships are straight, so test an adjacent block: fire at B5."
                  : "You hit B4 and B5, every block in the patrol boat. The enemy ship is sunk."
      }
      complete={stage === 5}
      onReset={() => setStage(0)}
    >
      <div className="tutorial-battleship">
        <div>
          <strong>Your fleet</strong>
          <button
            type="button"
            className={`tutorial-fleet-picker ${stage === 0 ? "hint" : ""}`}
            disabled={stage !== 0}
            onClick={() => setStage(1)}
          >
            <Ship />{" "}
            <span>
              <b>Patrol boat</b>
              <small>2 blocks · direct attack</small>
            </span>
          </button>
          <div className="tutorial-naval-grid" data-axis="A B C D E">
            {navalTutorialCells.map((cell, index) => (
              <button
                type="button"
                key={`fleet-${cell}`}
                aria-label={`Fleet coordinate ${cell.toUpperCase()}`}
                className={
                  shipCells.has(index)
                    ? `ship ${stage === 1 ? "hint" : ""} ${stage >= 2 ? "placed" : ""}`
                    : ""
                }
                onClick={() => index === 11 && stage === 1 && setStage(2)}
                disabled={stage !== 1}
              >
                {shipCells.has(index) && stage >= 2 ? <i /> : null}
              </button>
            ))}
          </div>
          <button
            type="button"
            className={`tutorial-lock ${stage === 2 ? "hint" : ""}`}
            disabled={stage !== 2}
            onClick={() => setStage(3)}
          >
            <LockKeyhole /> Lock fleet
          </button>
        </div>
        <div>
          <strong>Enemy waters</strong>
          <div className="tutorial-naval-grid">
            {navalTutorialCells.map((cell, index) => (
              <button
                type="button"
                key={`target-${cell}`}
                className={
                  index === 8 && stage === 3
                    ? "target hint"
                    : index === 9 && stage === 4
                      ? "target hint"
                      : index === 8 && stage === 4
                        ? "hit"
                        : (index === 8 || index === 9) && stage === 5
                          ? "hit"
                          : ""
                }
                aria-label={`Enemy coordinate ${cell.toUpperCase()}`}
                onClick={() => {
                  if (index === 8 && stage === 3) setStage(4);
                  else if (index === 9 && stage === 4) setStage(5);
                }}
                disabled={stage !== 3 && stage !== 4}
              >
                {(index === 8 && stage >= 4) || (index === 9 && stage === 5) ? <Crosshair /> : null}
              </button>
            ))}
          </div>
        </div>
      </div>
    </TutorialFrame>
  );
}

function DrawGuessTutorial() {
  const [lesson, setLesson] = useState<"draw" | "guess">("draw");
  const [points, setPoints] = useState<Array<{ x: number; y: number }>>([]);
  const [drawing, setDrawing] = useState(false);
  const [detected, setDetected] = useState(false);
  const [guess, setGuess] = useState("");
  const [guessStatus, setGuessStatus] = useState<"correct" | "wrong" | null>(null);
  useEffect(() => {
    if (lesson !== "draw" || points.length < 10 || drawing || detected) return;
    const timer = window.setTimeout(() => setDetected(true), 850);
    return () => window.clearTimeout(timer);
  }, [detected, drawing, lesson, points.length]);
  const addPoint = (event: PointerEvent<SVGSVGElement>) => {
    if (!drawing) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    setPoints((value) =>
      [
        ...value,
        {
          x: ((event.clientX - bounds.left) / bounds.width) * 300,
          y: ((event.clientY - bounds.top) / bounds.height) * 180,
        },
      ].slice(-180),
    );
  };
  return (
    <TutorialFrame
      title={lesson === "draw" ? "1. Draw for the local recognizer" : "2. Read and submit a guess"}
      prompt={
        lesson === "draw"
          ? detected
            ? "The on-device practice recognizer detected “sun”. Simple silhouettes and one distinctive detail are easier to recognise than shading."
            : points.length
              ? "Finish the stroke and give the local recognizer a moment to inspect it."
              : "Your word is SUN. Draw a large circle with short rays—do not write letters."
          : guessStatus === "correct"
            ? "Correct—SUN. The round accepts the normalised answer and reveals who guessed it."
            : guessStatus === "wrong"
              ? "Not quite. Look at the circle and rays, then type SUN and submit again."
              : "The bot drew a circle with rays. Type what you think it is, then submit before the timer ends."
      }
      complete={lesson === "guess" && guessStatus === "correct"}
      onReset={() => {
        setLesson("draw");
        setPoints([]);
        setDetected(false);
        setGuess("");
        setGuessStatus(null);
      }}
    >
      <div className="tutorial-draw">
        {lesson === "draw" ? (
          <>
            <span className="tutorial-secret">
              Your word: <strong>SUN</strong>
            </span>
            <svg
              viewBox="0 0 300 180"
              role="img"
              aria-label="Practice drawing canvas"
              onPointerDown={(event) => {
                event.currentTarget.setPointerCapture(event.pointerId);
                setDrawing(true);
                addPoint(event);
              }}
              onPointerMove={addPoint}
              onPointerUp={() => setDrawing(false)}
              onPointerCancel={() => setDrawing(false)}
            >
              <polyline points={points.map((point) => `${point.x},${point.y}`).join(" ")} />
            </svg>
            <div className="tutorial-ai-guess">
              <Bot />{" "}
              {detected ? (
                <strong>My guess: sun</strong>
              ) : (
                <span>
                  Recognising locally<span className="thinking-dots">…</span>
                </span>
              )}
            </div>
            {detected ? (
              <button
                type="button"
                className="tutorial-next-lesson"
                onClick={() => setLesson("guess")}
              >
                Next: guess a drawing <ArrowRight />
              </button>
            ) : null}
          </>
        ) : (
          <>
            <svg
              className="tutorial-bot-drawing"
              viewBox="0 0 300 180"
              role="img"
              aria-label="A simple drawing of a sun"
            >
              <circle cx="150" cy="90" r="38" />
              {sunRayAngles.map((angle) => {
                return (
                  <line
                    key={angle}
                    x1={150 + Math.cos(angle) * 52}
                    y1={90 + Math.sin(angle) * 52}
                    x2={150 + Math.cos(angle) * 72}
                    y2={90 + Math.sin(angle) * 72}
                  />
                );
              })}
            </svg>
            <form
              className="tutorial-guess-form"
              onSubmit={(event: FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                setGuessStatus(guess.trim().toLowerCase() === "sun" ? "correct" : "wrong");
              }}
            >
              <label htmlFor="tutorial-drawing-guess">Your guess</label>
              <div>
                <input
                  id="tutorial-drawing-guess"
                  value={guess}
                  onChange={(event) => setGuess(event.target.value)}
                  placeholder="Type what the bot drew"
                  autoComplete="off"
                />
                <button type="submit" disabled={!guess.trim()}>
                  Submit guess
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </TutorialFrame>
  );
}

function RockPaperScissorsTutorial() {
  const [choice, setChoice] = useState<"rock" | "paper" | "scissors" | null>(null);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!choice) return;
    const timer = window.setTimeout(() => setRevealed(true), 650);
    return () => window.clearTimeout(timer);
  }, [choice]);
  const result =
    !revealed || !choice ? null : choice === "paper" ? "win" : choice === "rock" ? "draw" : "loss";
  const resultText =
    result === "win"
      ? "You win — paper covers rock"
      : result === "draw"
        ? "Draw — rock matches rock"
        : result === "loss"
          ? "Bot wins — rock crushes scissors"
          : "";
  return (
    <TutorialFrame
      title="Learn the three-way match-up"
      prompt={
        !choice
          ? "The bot will throw rock. Choose the hand that beats it."
          : !revealed
            ? "Choice locked. Both hands reveal together so neither player gets an early look."
            : choice === "paper"
              ? "Paper covers rock—you win. Every sign beats one sign and loses to one."
              : "Rock beats scissors, and paper beats rock. Reset and choose paper to complete the lesson."
      }
      complete={revealed && choice === "paper"}
      onReset={() => {
        setChoice(null);
        setRevealed(false);
      }}
    >
      <div className="tutorial-rps">
        <div className="tutorial-rps-player">
          <span>You</span>
          <b>
            {choice === "rock"
              ? "✊"
              : choice === "paper"
                ? "✋"
                : choice === "scissors"
                  ? "✌️"
                  : "?"}
          </b>
        </div>
        <div className="tutorial-rps-player bot">
          <span>Topzle bot</span>
          <b>{revealed ? "✊" : "?"}</b>
        </div>
        {result ? (
          <div className={`tutorial-rps-result ${result}`} role="status">
            <strong>
              {result === "win" ? "YOU WIN" : result === "draw" ? "DRAW" : "BOT WINS"}
            </strong>
            <span>{resultText.split(" — ")[1]}</span>
          </div>
        ) : null}
        <div className="tutorial-rps-choices">
          {(["rock", "paper", "scissors"] as const).map((value) => (
            <button
              type="button"
              key={value}
              disabled={Boolean(choice)}
              onClick={() => setChoice(value)}
              className={value === "paper" && !choice ? "hint" : ""}
            >
              {value === "rock" ? "✊" : value === "paper" ? "✋" : "✌️"}
              <small>{value}</small>
            </button>
          ))}
        </div>
      </div>
    </TutorialFrame>
  );
}

function SnakesLaddersTutorial() {
  const [position, setPosition] = useState(1);
  const [phase, setPhase] = useState<0 | 1 | 2>(0);
  const [sequence, setSequence] = useState<number[]>([]);
  const [transportTarget, setTransportTarget] = useState<number | null>(null);
  const rolling = sequence.length > 0 || transportTarget !== null;
  const complete = phase === 2;
  useEffect(() => {
    if (sequence.length > 0) {
      const timer = window.setTimeout(() => {
        setPosition(sequence[0] ?? position);
        setSequence((steps) => steps.slice(1));
      }, 260);
      return () => window.clearTimeout(timer);
    }
    if (transportTarget === null) return;
    const timer = window.setTimeout(() => {
      setPosition(transportTarget);
      setPhase(transportTarget === 14 ? 1 : 2);
      setTransportTarget(null);
    }, 650);
    return () => window.clearTimeout(timer);
  }, [position, sequence, transportTarget]);
  const roll = () => {
    if (rolling || complete) return;
    if (phase === 0) {
      setSequence([2, 3, 4, 5]);
      setTransportTarget(14);
    } else {
      setSequence([15, 16, 17]);
      setTransportTarget(7);
    }
  };
  return (
    <TutorialFrame
      title="Count the move, then climb"
      prompt={
        complete
          ? "The goti landed on the snake’s head at 17, so the snake bit and carried it down to its tail at 7. Passing over 17 would be safe."
          : rolling
            ? `Your goti is moving one square at a time. Current square: ${position}.`
            : phase === 0
              ? "Tap the die to roll four. Count from 1 to the ladder foot at 5, then watch the climb to 14."
              : "You climbed from 5 to 14. Now roll three: landing on the snake head at 17 will trigger a bite."
      }
      complete={complete}
      onReset={() => {
        setPosition(1);
        setPhase(0);
        setSequence([]);
        setTransportTarget(null);
      }}
    >
      <div className="tutorial-snakes">
        <div className="tutorial-snakes-board">
          {[
            21, 22, 23, 24, 25, 20, 19, 18, 17, 16, 11, 12, 13, 14, 15, 10, 9, 8, 7, 6, 1, 2, 3, 4,
            5,
          ].map((value) => {
            return (
              <span
                key={value}
                className={`${value === 5 ? "ladder-foot" : value === 14 ? "ladder-top" : value === 17 ? "snake-head" : value === 7 ? "snake-tail" : ""} ${position === value ? "occupied" : ""}`}
              >
                <small>{value}</small>
                {position === value ? <b>1</b> : null}
              </span>
            );
          })}
          <svg className="tutorial-routes" viewBox="0 0 100 100" aria-hidden="true">
            <g className="tutorial-ladder-route" transform="rotate(-27 80 70)">
              <line x1="72" y1="91" x2="72" y2="48" />
              <line x1="80" y1="91" x2="80" y2="48" />
              {[54, 62, 70, 78, 86].map((y) => (
                <line className="rung" x1="70" y1={y} x2="82" y2={y} key={y} />
              ))}
            </g>
            <defs>
              <linearGradient id="tutorial-snake-green" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="#126b43" />
                <stop offset="0.5" stopColor="#42e69f" />
                <stop offset="1" stopColor="#0b4c31" />
              </linearGradient>
            </defs>
            <path className="tutorial-snake-shadow" d="M 70 70 C 48 61, 91 45, 70 31" />
            <path className="tutorial-snake-body" d="M 70 70 C 48 61, 91 45, 70 31" />
            <path className="tutorial-snake-belly" d="M 70 70 C 48 61, 91 45, 70 31" />
            <g className="tutorial-snake-head" transform="translate(70 29) rotate(-90)">
              <path d="M -5 -4 C 0 -7 7 -5 10 0 C 7 5 0 7 -5 4 Z" />
              <circle cx="4" cy="-2.5" r="1.1" />
              <circle cx="4" cy="2.5" r="1.1" />
              <path className="tongue" d="M 9 0 h 5 m -1 0 l 2 -2 m -2 2 l 2 2" />
            </g>
          </svg>
        </div>
        <button
          type="button"
          className={`tutorial-die four ${!rolling && !complete ? "hint" : ""}`}
          disabled={rolling || complete}
          onClick={roll}
          aria-label={phase === 0 ? "Roll a four" : "Roll a three"}
        >
          {tutorialPips.slice(0, phase === 0 ? 4 : 3).map((pip) => (
            <i key={pip} />
          ))}
        </button>
      </div>
    </TutorialFrame>
  );
}

export function InteractiveGameTutorial({ gameId }: { gameId: PlayableRoomGameId }) {
  switch (gameId) {
    case "tic-tac-toe":
      return <TicTacToeTutorial />;
    case "chess":
      return <ChessTutorial />;
    case "ludo":
      return <LudoTutorial />;
    case "connect-four":
      return <ConnectFourTutorial />;
    case "battleship":
      return <BattleshipTutorial />;
    case "draw-guess":
      return <DrawGuessTutorial />;
    case "rock-paper-scissors":
      return <RockPaperScissorsTutorial />;
    case "snakes-ladders":
      return <SnakesLaddersTutorial />;
    case "mafia":
      return <MafiaTutorial />;
  }
}
